import { USERS_BY_ROLE } from "@/api/queries/usersByRole";
import { useEffect } from "react";
import { useOrg } from "./useOrg";
import {
  UserRoleEnum,
  UserOmissionAttributeEnum,
} from "@/__generated__/graphql";
import { useReactiveVar, useLazyQuery, ReactiveVar } from "@apollo/client";
import { usersVar } from "@/reactiveVars/organization";
import { User } from "@/__generated__/types";

const PAGE_SIZE = 100;
const IMAGE_SIZE = 40;

// Move the default omissions outside the component to prevent recreation
export const DEFAULT_OMISSIONS: UserOmissionAttributeEnum[] = [
  UserOmissionAttributeEnum.Employee,
  UserOmissionAttributeEnum.FeaturedStudentOfficial,
  UserOmissionAttributeEnum.PermanentOrganizationOfficial,
];

interface useUsersByRolesProps {
  roles: UserRoleEnum[];
  omissions?: UserOmissionAttributeEnum[];
  userReactiveVar?: ReactiveVar<User[] | null>;
}

export const useUsersByRoles = ({
  roles,
  omissions = DEFAULT_OMISSIONS,
  userReactiveVar = usersVar,
}: useUsersByRolesProps): {
  data: Partial<User>[] | null;
  loading: boolean;
} => {
  const [getUsers] = useLazyQuery(USERS_BY_ROLE);
  const usersValue = useReactiveVar(userReactiveVar);
  const { org } = useOrg();

  // TODO: this can cause unnecessary rerenders; fix.
  useEffect(() => {
    userReactiveVar(null);
  }, [org, userReactiveVar]);

  useEffect(() => {
    if (usersValue !== null) return;
    if (!org) return;

    const fetchAllItems = async () => {
      let hasNextPage = true;
      let cursor = null;
      const pages = [];

      while (hasNextPage) {
        const { data } = await getUsers({
          variables: {
            orgId: org.id,
            after: cursor,
            first: PAGE_SIZE,
            roles,
            imageSize: IMAGE_SIZE,
            omissions,
          },
        });

        const node = data?.node;
        if (node?.__typename !== "Organization") {
          break;
        }
        const userConnection = node.users;

        const counselors =
          userConnection?.edges?.map((edge) => edge?.node).filter((m) => !!m) ||
          [];

        pages.push(counselors);
        hasNextPage = !!userConnection?.pageInfo.hasNextPage;
        cursor = userConnection?.pageInfo.endCursor;
      }
      userReactiveVar(pages.flat() as User[]);
    };
    fetchAllItems();
  }, [getUsers, org, omissions, roles, userReactiveVar, usersValue]);

  return { data: usersValue, loading: usersValue === null };
};
